import { BusFill18Icon } from "@vygruppen/spor-icon-react";
import { BussesPassengersCounter } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/components/BussesPassengersCounter";
import { BadgeConfig } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/types";
import { CAPACITY_PER_BUS } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";
import { AltTransStretchCoverage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { FC, useCallback, useMemo } from "react";
import {
  Badge,
  Box,
  DropsButton,
  Flex,
  Text,
  tokens,
} from "shared/components/Spor";
import { useTheme } from "styled-components";

interface StretchProgressCardProps {
  stretchCoverage: AltTransStretchCoverage;
  selectedStretch: AltTransStretchCoverage | null;
  setStretch: (stretch: AltTransStretchCoverage | null) => void;
}

export const StretchProgressCard: FC<StretchProgressCardProps> = ({
  stretchCoverage,
  selectedStretch,
  setStretch,
}) => {
  const theme = useTheme();

  const { totalStretchCoverage, maxPassengers } = stretchCoverage;
  const busesRequired =
    totalStretchCoverage.totalRequired ??
    Math.ceil(maxPassengers / CAPACITY_PER_BUS);

  const calculateSectionWidth = useCallback(
    (value: number) =>
      busesRequired > 0 ? `${(value / busesRequired) * 100}%` : "0%",
    [busesRequired],
  );

  const badges: BadgeConfig[] = useMemo(
    () => [
      { semantics: "alarm", count: totalStretchCoverage.stillRequired },
      { semantics: "warning", count: totalStretchCoverage.totalOrdered },
      { semantics: "info", count: totalStretchCoverage.totalConfirmed },
      { semantics: "success", count: totalStretchCoverage.totalOperational },
    ],
    [totalStretchCoverage],
  );

  const noBuses = useMemo(
    () => badges.every((badge) => badge.count === 0),
    [badges],
  );

  return (
    <DropsButton
      variant="ghost"
      gap={tokens.default.size.spacing[1]}
      alignItems="center"
      flexDirection="column"
      border="1px solid"
      py={tokens.default.size.spacing[1]}
      px={tokens.default.size.spacing[2]}
      borderRadius={tokens.default.size.spacing[1]}
      borderColor={theme.colorOutlineSecondary}
      width="100%"
      onClick={() =>
        setStretch(selectedStretch === stretchCoverage ? null : stretchCoverage)
      }
      backgroundColor={
        selectedStretch === stretchCoverage
          ? theme.colorSelected
          : theme.colorBackgroundSecondary
      }
    >
      <Flex alignItems="center" width="100%" height="28px" fontWeight="normal">
        <Text
          color={theme.colorTextMain}
          variant="xs"
          flex="1"
          textAlign="left"
        >
          {stretchCoverage.stretchName}
        </Text>
        <BussesPassengersCounter
          vehicles={busesRequired}
          passengers={maxPassengers}
          vehiclesFromMeasureCard={
            totalStretchCoverage.totalRequiredByMeasureCard
          }
        />
      </Flex>

      <Flex width="100%">
        {noBuses ? (
          <Box
            key="no-buses"
            borderRadius="2px"
            minWidth={tokens.default.size.spacing[7]}
            width="100%"
            textAlign="start"
            backgroundColor={theme.colorBackgroundMain}
            height={tokens.default.size.spacing[4]}
          />
        ) : (
          badges.map(
            ({ semantics, count }) =>
              count > 0 && (
                <Badge
                  key={semantics}
                  borderRadius="2px"
                  semantic={semantics}
                  minWidth={tokens.default.size.spacing[7]}
                  width={calculateSectionWidth(count)}
                  textAlign="start"
                  height={tokens.default.size.spacing[4]}
                >
                  <BusFill18Icon />
                  <Text variant="xs" mr="auto">
                    {count}
                  </Text>
                </Badge>
              ),
          )
        )}
      </Flex>
    </DropsButton>
  );
};
